// eslint-disable-next-line no-shadow
export enum RedirectKind {
  Internal = 'internal',
  External = 'external',
  Invalid = 'invalid',
}

export type ClassifiedRedirect =
  | { kind: RedirectKind.Internal; url: string }
  | { kind: RedirectKind.External; url: string }
  | { kind: RedirectKind.Invalid };

const ALLOWED_PROTOCOLS = ['http:', 'https:'];

const toInternalPath = (url: URL): string =>
  `${url.pathname}${url.search}${url.hash}`;

/**
 * Classifies a redirect target coming from an untrusted source (e.g. the
 * ElevenLabs agent): app-relative paths and same-origin URLs are Internal
 * (navigable with react-router), absolute http(s) URLs are External (new
 * tab), and everything else — javascript:/data: schemes, protocol-relative
 * URLs, garbage — is Invalid and must be ignored.
 */
export function classifyRedirectUrl(rawUrl: string): ClassifiedRedirect {
  const trimmed = typeof rawUrl === 'string' ? rawUrl.trim() : '';
  if (!trimmed) return { kind: RedirectKind.Invalid };

  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    const url = new URL(trimmed, window.location.origin);
    return { kind: RedirectKind.Internal, url: toInternalPath(url) };
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { kind: RedirectKind.Invalid };
  }

  if (!ALLOWED_PROTOCOLS.includes(url.protocol)) {
    return { kind: RedirectKind.Invalid };
  }

  if (url.origin === window.location.origin) {
    return { kind: RedirectKind.Internal, url: toInternalPath(url) };
  }

  return { kind: RedirectKind.External, url: url.href };
}
